const normalizeStatus = (status) => String(status || "").trim().toLowerCase();

export const hasActiveSuccessfulPlanPayment = (planPayment) =>
  Boolean(planPayment) &&
  (planPayment?.isSuccessful === true || planPayment?.isSuccessfull === true) &&
  planPayment?.isCanceled !== true &&
  planPayment?.isExpired !== true;

export const hasProvisionedSellerAccount = (value) =>
  Boolean(value?.seller?.id || value?.sellerId || value?.sellerAccountId);

export const isApprovedRequestStatus = (status) =>
  normalizeStatus(status) === "approved";

export const isPendingRequestStatus = (status) =>
  normalizeStatus(status) === "pending";

export const isRejectedRequestStatus = (status) =>
  normalizeStatus(status) === "rejected";

export const isFreePlan = (plan) =>
  plan?.isFree === true || Number(plan?.price ?? -1) === 0;

export const normalizeMarketplaceRequests = (responseData) => {
  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (Array.isArray(responseData?.requests)) {
    return responseData.requests;
  }

  return [];
};

const getRequestTimestamp = (request) => {
  const rawValue = request?.updatedAt || request?.createdAt || "";
  const timestamp = Date.parse(rawValue);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

export const getLatestMarketplaceRequest = (responseData) =>
  normalizeMarketplaceRequests(responseData)
    .slice()
    .sort((left, right) => getRequestTimestamp(right) - getRequestTimestamp(left))[0] ||
  null;

export const hasApprovedMarketplaceAccess = (user) =>
  hasProvisionedSellerAccount(user);

export const resolveMarketplaceAccessState = ({
  user,
  profile,
  activePlanPayment,
}: any) => {
  const sellerData = profile?.seller || null;
  const latestRequest = activePlanPayment?.request || null;
  const activePlan =
    activePlanPayment?.plan || latestRequest?.plan || sellerData?.plan || null;

  const requestStatus = latestRequest?.status || user?.requestStatus || null;
  const requestIsPaid =
    latestRequest?.isPaid ??
    (hasActiveSuccessfulPlanPayment(activePlanPayment)
      ? true
      : user?.requestIsPaid ?? null);

  const hasSellerAccount = hasProvisionedSellerAccount({
    seller: sellerData,
    sellerId: profile?.sellerId || user?.sellerId,
    sellerAccountId: user?.sellerAccountId,
  });

  if (hasSellerAccount) {
    return {
      status: "approved",
      activePlan,
      latestRequest,
      requestStatus,
      requestIsPaid,
      hasMarketplaceAccess: true,
    };
  }

  if (isRejectedRequestStatus(requestStatus)) {
    return {
      status: "rejected",
      activePlan,
      latestRequest,
      requestStatus,
      requestIsPaid,
      hasMarketplaceAccess: false,
    };
  }

  if (isPendingRequestStatus(requestStatus)) {
    return {
      status: "pending",
      activePlan,
      latestRequest,
      requestStatus,
      requestIsPaid,
      hasMarketplaceAccess: false,
    };
  }

  if (isApprovedRequestStatus(requestStatus)) {
    return {
      status: "approved",
      activePlan,
      latestRequest,
      requestStatus,
      requestIsPaid,
      hasMarketplaceAccess: true,
    };
  }

  if (requestIsPaid) {
    return {
      status: "paid",
      activePlan,
      latestRequest,
      requestStatus,
      requestIsPaid,
      hasMarketplaceAccess: false,
    };
  }

  return {
    status: "needs-plan",
    activePlan,
    latestRequest,
    requestStatus,
    requestIsPaid,
    hasMarketplaceAccess: false,
  };
};

export const getDuplicateRequestResolution = (message) => {
  const normalizedMessage = String(message || "").trim().toLowerCase();

  if (!normalizedMessage) {
    return null;
  }

  if (normalizedMessage.includes("already approved")) {
    return "approved";
  }

  if (
    normalizedMessage.includes("already pending") ||
    normalizedMessage.includes("already submitted") ||
    normalizedMessage.includes("under review") ||
    normalizedMessage.includes("already exists") ||
    normalizedMessage.includes("another request")
  ) {
    return "pending";
  }

  return null;
};